<script lang="ts">
import { defineComponent } from 'vue';
import { usePoolsStore } from '@/stores/pools';
import {
  addPoolMarket,
  deletePoolMarket,
  fetchPool,
  fetchPoolAvailableEvents,
} from '@/api/admin-be';
import { mapState } from 'pinia';
import { catchError } from '@/helpers/toast';
import EventFilters from '@/components/pools/pool/event-filters.vue';
import Events from '@/components/pools/pool/events.vue';
import Pagination from '@/components/pagination.vue';
import PoolDetails from '@/components/pools/pool/pool-details.vue';
import EventMarkets from '@/components/pools/pool/event-markets.vue';
import Headline from '@/components/headline.vue';

export default defineComponent({
  name: 'pool',
  components: { Headline, EventMarkets, PoolDetails, Pagination, Events, EventFilters },
  computed: {
    ...mapState(usePoolsStore, ['currentPoolId']),
  },
  data() {
    return {
      loading: false,
      pool: null,

      sportId: null,
      regionId: null,
      competitionId: null,
      fixtureId: null,
      fromDate: null as null | Date,
      toDate: null as null | Date,
      inputDiff: null,

      eventsOffset: 0,
      eventsPageSize: 10,
      eventsCount: 0,
      events: null,
    };
  },
  methods: {
    fetchEvents() {
      fetchPoolAvailableEvents({
        sportIds: this.sportId,
        regionIds: this.regionId,
        competitionIds: this.competitionId,
        offset: this.eventsOffset,
        pageSize: this.eventsPageSize,
        fromDate: this.fromDate?.toISOString() || undefined,
        toDate: this.toDate?.toISOString() || undefined,
        oddsDiff: this.inputDiff,
      }).then((res) => {
        this.events = res.fixtures;
        this.eventsCount = res.count;
      });
    },
    flipEventsPage(newOffset: number) {
      this.eventsOffset = newOffset;
      this.fetchEvents();
    },
    addPoolMarket(marketId: number) {
      if (this.currentPoolId) {
        this.loading = true;
        addPoolMarket(this.currentPoolId, marketId)
          .then(() => this.fetchPool())
          .catch((error) => catchError('Ошибка добавления маркета', error))
          .finally(() => (this.loading = false));
      }
    },
    deletePoolMarket(marketId: number) {
      if (this.currentPoolId) {
        deletePoolMarket(this.currentPoolId, marketId)
          .then(() => this.fetchPool())
          .catch((error) => catchError('Ошибка удаления маркета', error))
          .finally(() => (this.loading = false));
      }
    },
    fetchPool() {
      if (this.currentPoolId) {
        this.loading = true;
        fetchPool(this.currentPoolId)
          .then((pool) => {
            this.pool = pool;
          })
          .catch((error) => catchError('Ошибка загрузки пула', error))
          .finally(() => (this.loading = false));
      } else {
        this.pool = null;
      }
    },
  },
  mounted() {
    this.fetchPool();
  },
  watch: {
    sportId() {
      this.fetchEvents();
    },
    regionId() {
      this.fetchEvents();
    },
    competitionId() {
      this.fetchEvents();
    },
    fromDate() {
      this.fetchEvents();
    },
    toDate() {
      this.fetchEvents();
    },
    inputDiff() {
      this.fetchEvents();
    },
    currentPoolId() {
      this.fetchPool();
    },
  },
});
</script>

<template>
  <template v-if="currentPoolId">
    <headline class="my-4">
      Настройка событий пула
      <template #counter>{{ pool?.name }}</template>
    </headline>

    <pool-details :pool @deleteMarket="deletePoolMarket" />

    <headline class="my-4">
      Подбор событий
    </headline>

    <event-filters
      v-model:sport-id="sportId"
      v-model:region-id="regionId"
      v-model:competition-id="competitionId"
      v-model:from-date="fromDate"
      v-model:to-date="toDate"
      v-model:input-diff="inputDiff"
    />

    <div class="grid grid-cols-[2fr_1fr]">
      <div class="text-center">
        <events :events :pageSize="eventsPageSize" v-model:fixtureId="fixtureId" />
        <Pagination
          :offset="eventsOffset"
          :size="eventsPageSize"
          :count="eventsCount"
          @flip="flipEventsPage"
        />
      </div>
      <div>
        <event-markets :fixtureId="fixtureId" @addMarket="addPoolMarket" />
      </div>
    </div>
  </template>
</template>

<style scoped lang="scss"></style>
